import type { ReactNode } from 'react';
import { Calculator, Check, ChevronRight, MinusIcon, PlusIcon } from 'lucide-react';

import { PromotionPrice } from '@/modules/stores/components/promotion-price';
import { AddonsItem } from '@/modules/stores/components/addons';
import { Progress } from '@/shared/components/ui/progress';
import { Quantity } from '@/shared/components/ui/quantity';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/lib/utils';
import { enhancedToast } from '@/shared/utils/enhanced-toast';
import { formatPrice } from '@/shared/utils/format-price';
import { getImageUrl } from '@/shared/utils/image';

import type { Addon, SelectedAddon } from '../../types/addons';
import type { ComboGroup, ComboGroupOption } from '../../types/get-store-items';
import {
  getAddonLimitLabel,
  getAddonQuantity,
  getCalculationTypeLabel,
  getCategoryLimitMessage,
  getMaxForAddon,
  getPriceCalculationType,
  isAddonSelected,
} from '../../utils/addon';
import {
  calculateComboChoicesPrice,
  findComboSelection,
  getComboGroupRuleLabel,
  getComboGroupStepKey,
  getComboStepAddonCategories,
  getComboStepAddons,
  getComboStepCount,
  getComboStepRuleLabel,
  getComboStepTitle,
  getMaxChoices,
  getOptionAdditionalPrice,
  getOptionMaxQuantity,
  isComboStepRequired,
  type ComboStep,
} from '../../utils/combo';
import { getDisplayPrice } from '../../utils/display-price';
import { useProductDrawerModel } from './use-product-drawer-model';

type ComboWizardProps = ReturnType<typeof useProductDrawerModel>;

/**
 * Montagem do combo de escolha em etapas (PRAT-20): uma etapa por grupo, uma
 * por categoria de complementos do produto escolhido (grupo personalizável) e o
 * resumo no fim. As regras (mín./máx., preço, payload) são as de `utils/combo`.
 */
export function ComboWizard(props: ComboWizardProps) {
  const {
    product,
    totalPrice,
    comboSteps,
    comboStep,
    comboStepIndex,
    comboStepBlocker,
    handleComboNext,
    handleComboBack,
    handleAdvanceToDrawer,
    isComboValid,
    isEditMode,
    scrollContainerRef,
  } = props;

  if (!product || !comboStep) return null;

  const isSummary = comboStep.kind === 'summary';
  const total = comboSteps.length;

  return (
    <div className="flex flex-col h-full bg-white rounded-t-[32px]" data-combo-wizard>
      {/** Progresso (o botão de fechar do drawer fica à esquerda) */}
      <div className="pt-5 pb-3 pl-16 pr-4 border-b flex-shrink-0">
        <p className="text-gray-500 text-xs">
          Etapa {comboStepIndex + 1} de {total}
        </p>
        <p className="text-night text-sm font-bold truncate">{getComboStepTitle(comboStep)}</p>
        <Progress
          value={((comboStepIndex + 1) / total) * 100}
          className="mt-2 h-1.5"
          aria-label={`Etapa ${comboStepIndex + 1} de ${total}`}
        />
      </div>

      <div className="flex-1 overflow-y-auto" data-scroll-container ref={scrollContainerRef}>
        {comboStepIndex === 0 && <ComboProductHeader product={product} />}
        {isSummary ? <ComboSummary {...props} /> : <ComboStepBody {...props} step={comboStep} />}
      </div>

      {/** Rodapé fixo: Voltar / Próximo (ou Adicionar no resumo) */}
      <div className="bg-white border-t py-3 px-4 flex-shrink-0 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        {!isSummary && comboStepBlocker && (
          <p className="text-gray-500 text-xs text-center pb-2" aria-live="polite">
            {comboStepBlocker}
          </p>
        )}
        <div className="flex items-center gap-3">
          {comboStepIndex > 0 && (
            <button
              type="button"
              onClick={handleComboBack}
              className="h-12 px-5 rounded-lg border border-gray-300 text-night text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Voltar
            </button>
          )}
          {isSummary ? (
            <button
              type="button"
              onClick={handleAdvanceToDrawer}
              aria-disabled={!isComboValid}
              className={cn(
                'flex-1 bg-secondary hover:bg-secondary/95 text-secondary-foreground h-12 px-4 rounded-lg text-sm font-bold transition-colors clickable-button',
                !isComboValid && 'opacity-60'
              )}
            >
              {isEditMode ? 'Salvar' : 'Adicionar'} • {formatPrice(totalPrice.toString())}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleComboNext}
              disabled={!!comboStepBlocker}
              className="flex-1 bg-secondary hover:bg-secondary/95 text-secondary-foreground h-12 px-4 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Cabeçalho do produto na primeira etapa: foto, nome, descrição e preço base. */
function ComboProductHeader({ product }: { product: NonNullable<ComboWizardProps['product']> }) {
  const { price, regularPrice, hasRequiredAddons } = getDisplayPrice(product);

  return (
    <div className="flex items-start gap-3 px-4 pt-4">
      <div className="w-20 h-20 rounded-3xl overflow-hidden flex-shrink-0 bg-gray-100">
        <img
          src={getImageUrl(product.image)}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={e => {
            e.currentTarget.src = '/fallback.svg';
          }}
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0 gap-1">
        <h1 className="font-bold text-lg text-night break-words">{product.name}</h1>
        {product.description && (
          <p className="text-gray-500 text-xs line-clamp-3">{product.description}</p>
        )}
        <PromotionPrice
          price={price}
          regularPrice={regularPrice}
          prefix={hasRequiredAddons ? 'A partir de' : undefined}
          priceClassName="text-primary"
        />
      </div>
    </div>
  );
}

function ComboStepBody(props: ComboWizardProps & { step: ComboStep }) {
  const { step, comboSelections, selectedAddons, product, comboStepBlocker, animatingBadge } =
    props;
  const productAddonCategories = product?.addon_categories ?? [];
  const { count, max } = getComboStepCount(
    step,
    comboSelections,
    selectedAddons,
    productAddonCategories
  );
  const required = isComboStepRequired(step);
  const category = step.kind === 'summary' || step.kind === 'group' ? null : step.category;
  const calcType = category ? getPriceCalculationType(category) : 0;

  return (
    <div className="p-4 space-y-3" data-category-id={step.key}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          {step.kind === 'choice-addons' && (
            <p className="text-gray-500 text-xs font-semibold truncate">{step.option.name}</p>
          )}
          <h2 className="text-night text-lg font-bold break-words">
            {step.kind === 'group' ? step.group.name : category?.name}
          </h2>
          <p className="text-gray-500 text-xs">
            {getComboStepRuleLabel(step)}
            {Number.isFinite(max) ? ` · ${count} de ${max}` : count > 0 ? ` · ${count}` : ''}
          </p>
          {calcType !== 0 && (
            <span className="text-blue-600 text-xs font-medium flex items-center gap-1">
              <Calculator className="w-3 h-3" />
              {getCalculationTypeLabel(calcType)}
            </span>
          )}
        </div>
        {required &&
          (comboStepBlocker ? (
            <span
              className={cn(
                'shrink-0 inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] bg-black text-gray-100 font-semibold',
                animatingBadge === step.key && 'animate-shake'
              )}
              data-mandatory-badge
            >
              OBRIGATÓRIO
            </span>
          ) : (
            <Check className="shrink-0 w-5 h-5 text-green-700 animate-in fade-in duration-300" />
          ))}
      </div>

      <div className="flex flex-col gap-2">
        {step.kind === 'group'
          ? step.group.options.map(option => (
              <ComboOptionCard key={option.id} {...props} group={step.group} option={option} />
            ))
          : category?.addons.map(addon => (
              <ComboAddonCard key={addon.id} {...props} step={step} addon={addon} />
            ))}
      </div>
    </div>
  );
}

function OptionCard({
  selected,
  onClick,
  children,
  control,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  control: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 w-full rounded-2xl border p-3 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-gray-200'
      )}
    >
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        {children}
      </div>
      {control}
    </div>
  );
}

function ComboOptionCard({
  group,
  option,
  comboSelections,
  handleToggleComboOption,
  handleComboOptionQuantityChange,
}: ComboWizardProps & { group: ComboGroup; option: ComboGroupOption }) {
  const selection = findComboSelection(comboSelections, group.id, option.item_id);
  const isSingleChoice = getMaxChoices(group) === 1;
  const maxQuantity = getOptionMaxQuantity(group, option.item_id, comboSelections);
  const additionalPrice = getOptionAdditionalPrice(option);

  // Escolha única troca a opção; nas demais o toque só marca (o contador cuida
  // das quantidades) e avisa quando o grupo já está no teto.
  const handleClick = () => {
    if (!isSingleChoice && selection) return;
    if (!isSingleChoice && maxQuantity < 1) {
      enhancedToast.warning('Limite de escolhas atingido', {
        description: `${group.name}: ${getComboGroupRuleLabel(group).toLowerCase()}`,
        id: `combo-limit-${group.id}`,
      });
      return;
    }
    handleToggleComboOption(group, option.item_id);
  };

  return (
    <OptionCard
      selected={!!selection}
      onClick={handleClick}
      control={
        isSingleChoice ? (
          <input
            type="radio"
            id={`combo-${group.id}-${option.item_id}`}
            name={`combo-group-${group.id}`}
            value={option.item_id}
            checked={!!selection}
            onChange={() => handleToggleComboOption(group, option.item_id)}
            // Reclicar a opção marcada não dispara `change`; o onClick desmarca.
            onClick={() => {
              if (selection) handleToggleComboOption(group, option.item_id);
            }}
            className="shrink-0 self-center w-5 h-5 accent-primary"
          />
        ) : (
          <Quantity
            value={selection?.quantity ?? 0}
            max={maxQuantity}
            onValueChange={value => handleComboOptionQuantityChange(group, option.item_id, value)}
            className="w-10 h-10 self-center"
          />
        )
      }
    >
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-3xl overflow-hidden flex-shrink-0 bg-gray-100">
          <img
            src={getImageUrl(option.image)}
            alt={option.name}
            className="w-full h-full object-cover object-center"
            onError={e => {
              e.currentTarget.src = '/fallback.svg';
            }}
          />
        </div>
        <div className="flex flex-col flex-1 min-w-0 gap-px">
          <h3 className="text-night text-sm font-bold line-clamp-2 break-words">
            {option.quantity > 1 ? `${option.quantity}x ` : ''}
            {option.name}
          </h3>
          {additionalPrice > 0 && (
            <p className="text-night text-xs font-semibold">
              + {formatPrice(additionalPrice.toString())}
            </p>
          )}
          {option.description && (
            <p className="text-gray-500 text-xs font-normal line-clamp-2">{option.description}</p>
          )}
        </div>
      </div>
    </OptionCard>
  );
}

function ComboAddonCard({
  step,
  addon,
  comboSelections,
  selectedAddons,
  product,
  handleComboChoiceAddonChange,
  handleToggleRadioAddon,
  handleAddonChange,
}: ComboWizardProps & { step: ComboStep; addon: Addon }) {
  if (step.kind !== 'choice-addons' && step.kind !== 'product-addons') return null;

  const { category } = step;
  const categories = getComboStepAddonCategories(step, product?.addon_categories ?? []);
  const addons: SelectedAddon[] = getComboStepAddons(step, comboSelections, selectedAddons);
  const selectMax = category.select_max;
  const maxForThisAddon = getMaxForAddon(addon.id, category.id, selectMax, addons, categories);
  const selected = isAddonSelected(addon.id, addons);
  const current = getAddonQuantity(addon.id, addons);
  const isRadio = selectMax === 1;

  const change = (quantity: number) => {
    if (step.kind === 'choice-addons') {
      handleComboChoiceAddonChange(
        step.group.id,
        step.option.item_id,
        categories,
        addon.id,
        quantity,
        isRadio,
        category.id
      );
    } else if (isRadio) {
      handleToggleRadioAddon(addon.id, category.id);
    } else {
      handleAddonChange(addon.id, quantity, category.id, selectMax);
    }
  };

  const handleClick = () => {
    if (isRadio) {
      change(selected ? 0 : 1);
      return;
    }
    if (current < maxForThisAddon) {
      change(current + 1);
      return;
    }
    if (addon.select_max && current >= addon.select_max) {
      enhancedToast.warning('Limite do complemento atingido', {
        description: getAddonLimitLabel(addon.select_max),
        id: `addon-item-limit-${step.key}-${addon.id}`,
      });
      return;
    }
    if (selectMax > 0) {
      enhancedToast.warning('Limite de complementos atingido', {
        description: getCategoryLimitMessage(selectMax),
        id: `addon-limit-${step.key}`,
      });
    }
  };

  return (
    <OptionCard
      selected={selected}
      onClick={handleClick}
      control={
        isRadio ? (
          <input
            type="radio"
            id={`${step.key}-${addon.id}`}
            // Nome por etapa: a mesma categoria pode aparecer em mais de uma escolha.
            name={`addon-${step.key}`}
            value={addon.id}
            checked={selected}
            onChange={() => change(1)}
            onClick={() => {
              if (selected) change(0);
            }}
            className="shrink-0 self-center w-5 h-5 accent-primary"
          />
        ) : (
          <Quantity
            value={current}
            max={maxForThisAddon}
            onValueChange={value => change(value)}
            className="w-10 h-10 self-center"
          />
        )
      }
    >
      <AddonsItem addon={addon} />
    </OptionCard>
  );
}

function formatAddons(addons: { name: string; quantity: number }[]) {
  return addons.map(a => `${a.quantity > 1 ? `${a.quantity}x ` : ''}${a.name}`).join(', ');
}

/** Resumo: cada escolha (toque volta à etapa), observação, quantidade e total. */
function ComboSummary({
  product,
  comboGroups,
  comboChoices,
  comboSteps,
  selectedAddons,
  totalPrice,
  watch,
  updateObservations,
  handleIncrementQuantity,
  handleDecrementQuantity,
  handleComboGoToStep,
}: ComboWizardProps) {
  const productAddonSteps = comboSteps.filter(step => step.kind === 'product-addons');

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-night text-lg font-bold">{product?.name}</h2>

      <ul className="flex flex-col divide-y rounded-2xl border border-gray-200">
        {comboGroups.map(group => {
          const choices = comboChoices.filter(choice => choice.group_id === group.id);

          return (
            <li key={group.id}>
              <button
                type="button"
                onClick={() => handleComboGoToStep(getComboGroupStepKey(group.id))}
                className="flex items-center gap-3 w-full p-3 text-left hover:bg-gray-50"
              >
                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span className="text-gray-500 text-xs">{group.name}</span>
                  {choices.length === 0 && (
                    <span className="text-gray-400 text-sm">Nenhuma escolha</span>
                  )}
                  {choices.map(choice => {
                    const extra = calculateComboChoicesPrice([choice]);
                    return (
                      <div key={choice.item_id} className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-night text-sm font-semibold break-words">
                            {choice.quantity > 1 ? `${choice.quantity}x ` : ''}
                            {choice.name}
                          </p>
                          {choice.addons.length > 0 && (
                            <p className="text-gray-500 text-xs break-words">
                              {formatAddons(choice.addons)}
                            </p>
                          )}
                        </div>
                        {extra > 0 && (
                          <span className="shrink-0 text-night text-xs font-semibold">
                            + {formatPrice(extra.toString())}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            </li>
          );
        })}

        {productAddonSteps.map(step => {
          if (step.kind !== 'product-addons') return null;
          const chosen = selectedAddons.filter(addon =>
            step.category.addons.some(item => item.id === addon.id)
          );

          return (
            <li key={step.key}>
              <button
                type="button"
                onClick={() => handleComboGoToStep(step.key)}
                className="flex items-center gap-3 w-full p-3 text-left hover:bg-gray-50"
              >
                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span className="text-gray-500 text-xs">{step.category.name}</span>
                  <span className={cn('text-sm', chosen.length ? 'text-night' : 'text-gray-400')}>
                    {chosen.length ? formatAddons(chosen) : 'Nenhuma escolha'}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>

      <div className="space-y-2" data-observations-section>
        <p className="text-night text-sm font-bold">Observações</p>
        <Textarea
          placeholder="Ex: Tirar calabresa, ovo, batata palha."
          value={watch('observations')}
          onChange={e => updateObservations(e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-night text-sm font-bold">Quantidade</p>
        <div className="flex items-center border border-gray-300 rounded-lg">
          <button
            type="button"
            onClick={handleDecrementQuantity}
            aria-label="Diminuir quantidade"
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
            disabled={watch('quantity') <= 1}
          >
            <MinusIcon className="w-5 h-5" />
          </button>
          <span className="w-10 h-10 flex items-center justify-center text-base font-semibold border-x border-gray-300">
            {watch('quantity')}
          </span>
          <button
            type="button"
            onClick={handleIncrementQuantity}
            aria-label="Aumentar quantidade"
            className="w-10 h-10 flex items-center justify-center text-primary hover:bg-gray-50 transition-colors clickable-small"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between border-t pt-3">
        <p className="text-night text-base font-bold">Total</p>
        <p className="text-night text-base font-bold">{formatPrice(totalPrice.toString())}</p>
      </div>
    </div>
  );
}
